import { useState, useEffect, useRef, useContext, useLayoutEffect } from 'react'

import { utilService } from '../../common/services/util.service'
import { FullPageLoader } from '../../common/components/full-page-loader'
import { Loader } from '../../common/components/loader'
import { ReportPageContextProvider } from '../contexts/report-page.context'
import { RouterContext } from '../../common/contexts/routerContext'
import { REPORT_VIEWER_MODE } from '../constants'
import { PageControlBar } from '../blocks/page-control-bar'
import { FloatingBackLink } from '../../common/components/floating-back-link'
import { ReportPageLayout } from './report-page-layout'
import { useStyles } from './executive-summary-page.style'
import { Header, PageSection, PageSectionPlaceholder, ProgressSidebar } from './executive-summary.components'
import { reportPageService } from '../report-page.service'

// Top action bar (80) + comment icon block action (40)
const TOP_CONTAINER_PADDING = 80 + 40

const breakpoints = {
  1550: 'lg',
  1250: 'md',
  1000: 'sm'
}

function getCurrentBreakpoint () {
  const breakpointsArr = Object.keys(breakpoints)
  const filteredBreakpoints = breakpointsArr.filter((bp) => bp > window.innerWidth)
  if (filteredBreakpoints.length) {
    const breakpoint = Math.min(...filteredBreakpoints)
    return breakpoints[breakpoint]
  }
}

let blockResizeObserver
let blockResizeElements
export const ExecutiveSummaryPage = function ({ report, mode, highlightBlockId, highlightPageProperty }) {
  const blockRefs = useRef({})
  const headerRef = useRef()
  const containerRef = useRef()

  const [initialPagesCalculated, setInitialPagesCalculated] = useState(false)
  const [currentBreakpoint, setCurrentBreakpoint] = useState(getCurrentBreakpoint())

  const [pageSections, setPageSections] = useState([])
  const [containerHeight, setContainerHeight] = useState(window.innerHeight - TOP_CONTAINER_PADDING)

  const classes = useStyles({ topPadding: TOP_CONTAINER_PADDING })
  const [layout, setLayout] = useState()
  const contentRef = useRef()
  const routerContext = useContext(RouterContext)

  function showHeader () {
    if (headerRef && headerRef.current) {
      headerRef.current.classList.add('show')
    }
  }

  function hideHeader () {
    if (headerRef && headerRef.current) {
      headerRef.current.classList.remove('show')
    }
  }

  /**
   * Function for setting/hiding class for showing a page section.
   * This uses DOM manipulation instead of react state because re-renders
   * within intersection observers causes slowness in certain browsers
   * @param {string} blockKey - The blockKey corresponding with the page section to show
   */
  const setPageSectionShowing = (blockKey) => {
    for (let pageSection of pageSections) {
      if (pageSection.key === blockKey) {
        pageSection.ref.current.parentNode.classList.add('show')
      } else {
        pageSection.ref.current.parentNode.classList.remove('show')
      }
    }

    // Update progress sidebar DOM appropriately.
    // Normally this would be managed using state passed down to the ProgressSidebar component; however,
    // there is a major performance issue with updating state within intersection observers that is
    // circumvented by doing direct DOM manipulations instead.
    const pageSectionIndex = pageSections.findIndex((pageSection) => pageSection.key === blockKey)
    const activeProgressSidebarItem = document.querySelector(`.${classes.progressSidebarItem}.active`)
    if (activeProgressSidebarItem) {
      activeProgressSidebarItem.classList.remove('active')
    }
    document.querySelectorAll(`.${classes.progressSidebarItem}.borderHighlight`).forEach((progressSidebarItemElement) => {
      progressSidebarItemElement.classList.remove('borderHighlight')
    })
    let activeProgressItemOffsetTop = 0
    if (pageSectionIndex !== -1) {
      const progressSidebarItems = document.querySelectorAll(`.${classes.progressSidebarItem}`)
      for (let i = 0; i <= pageSectionIndex; i++) {
        const progressSidebarItem = progressSidebarItems[i]
        progressSidebarItem.classList.add('borderHighlight')
        if (i === pageSectionIndex) {
          progressSidebarItem.classList.add('active')
          activeProgressItemOffsetTop = progressSidebarItem.offsetTop
        }
      }
    }
    const progressSidebarActiveTracker = document.querySelector(`.${classes.progressSidebarActiveTracker}`)
    if (progressSidebarActiveTracker && !progressSidebarActiveTracker.classList.contains('position-state-simple')) {
      progressSidebarActiveTracker.style.height = `${activeProgressItemOffsetTop}px`
    }
  }

  const handleBlockHeightChanges = (blockRefs) => {
    blockResizeObserver = new ResizeObserver(() => {
      calculatePageSections(layout)
    })

    blockResizeElements = Object.values(blockRefs.current).map((block) => block.current)
    blockResizeElements.forEach((block) => {
      blockResizeObserver.observe(block)
    })
  }

  useEffect(() => {
    if (headerRef && headerRef.current) {
      setTimeout(() => showHeader(), 500)
    }
  }, [headerRef])

  useLayoutEffect(() => {
    const layout = report.layout || {}
    layout.blocks = reportPageService.initializeLayoutBlocks(layout)
    setLayout(layout)

    const windowResizeHandler = utilService.debounce(() => {
      setContainerHeight(window.innerHeight - TOP_CONTAINER_PADDING)
      calculatePageSections(layout)
      setCurrentBreakpoint(getCurrentBreakpoint())
    })
    window.addEventListener('resize', windowResizeHandler)

    return () => {
      window.removeEventListener('resize', windowResizeHandler)
      if (blockResizeObserver && blockResizeElements) {
        blockResizeElements.forEach((block) => {
          blockResizeObserver.unobserve(block)
        })
        blockResizeObserver = null
        blockResizeElements = null
      }
    }
  }, [report])

  const scrollToHeader = () => {
    setPageSectionShowing()
    showHeader()
    containerRef.current.scrollTo({ top: 0, behavior: 'auto' })
  }

  const scrollToSection = (i, { behavior } = {}) => {
    const pageSection = pageSections && pageSections[i]
    if (pageSection) {
      hideHeader()
      setPageSectionShowing(pageSection.key)

      if (pageSection.ref && pageSection.ref.current) {
        behavior = behavior || 'smooth'
        // First scroll the placeholders to the correct position within the scroll container
        containerRef.current.scrollTo({ top: pageSection.offset + pageSection.scrollHeight / 2, behavior })

        // Then position the visible page sections
        let block = (pageSection.renderType === 'scrollable') ? 'start' : 'center'
        pageSection.ref.current.scrollIntoView({ behavior, block })
      }
    }
  }

  useEffect(() => {
    if (layout && highlightBlockId) {
      const index = (pageSections || []).findIndex(({ key }) => highlightBlockId === key)
      if (index >= 0) {
        scrollToSection(index, { behavior: 'auto' })
      }
    }
  }, [ layout, highlightBlockId ])

  useEffect(() => {
    if (layout && highlightPageProperty) {
      scrollToHeader()
      setPageSectionShowing()
    }
  }, [ layout, highlightPageProperty ])

  useEffect(() => {
    if (layout && layout.blocks.length === 0 && !initialPagesCalculated) {
      setInitialPagesCalculated(true)
      scrollToHeader()
    }
  }, [ layout ])

  if (!layout) {
    if (mode === REPORT_VIEWER_MODE.READ) {
      return <FullPageLoader />
    } else {
      return (
        <div className="page-content-center">
          <Loader />
        </div>
      )
    }
  }

  /**
   * @typedef {Object} PageSection
   * @property {string} key - The block key
   * @property {Object} ref - The reference of the block
   * @property {number} height - The height of the page section
   * @property {number} scrollHeight - The height to scroll for the section
   * @property {string} renderType - "sticky" or "scrollable". Sticky sections are smaller than the window size and scrollable elements sections larger
   * @property {number} offset - The accumulation of scrollHeight before this page section. Important for calculating where to place "scrollable" sections using absolute positioning
   * @property {number} scrollableOffset - The amount of top padding to apply for "scrollable" sections
   * @property {number} marginTopOffset - top margin offset to apply for an element. Useful for bringing the first section up higher so it display soon after scrolling past the header
   */

  /**
   * Function for calculating page section details when the page loads or on window dimension changes.
   * @param {Object} layout - The layout object of the report
   * @returns {Array<PageSection>} The array of page sections
   */
  function calculatePageSections (layout) {
    const containerHeight = window.innerHeight - TOP_CONTAINER_PADDING
    let offset = containerHeight - (containerHeight * 0.6) // Initial offset accounts for header
    const blockRefEntries = Object.entries(blockRefs.current)
    let pageSectionArray = blockRefEntries.reduce((acc, [key, blockRef]) => {
      const idx = layout.blocks.findIndex((b) => b.key === key)
      if (idx !== -1) {
        const blockHeight = blockRef && blockRef.current && blockRef.current.clientHeight
        const renderType = blockHeight >= containerHeight ? 'scrollable' : 'sticky'
        let scrollableOffset = 0
        if (renderType === 'scrollable') {
          scrollableOffset = containerHeight * 0.3
        }
        acc[idx] = {
          key,
          ref: blockRef,
          renderType,
          height: renderType === 'scrollable' ? blockHeight : containerHeight - TOP_CONTAINER_PADDING,
          scrollHeight: renderType === 'scrollable' ? blockHeight + scrollableOffset - containerHeight + scrollableOffset + TOP_CONTAINER_PADDING * 2 : containerHeight * 1.5,
          scrollableOffset
        }
      }
      return acc
    }, Array.from(Array(blockRefEntries.length)))
      .map((pageSection, i) => {
        const result = {
          offset,
          marginTopOffset: i === 0 ? -1 * containerHeight * 0.6 : 0,
          ...pageSection
        }
        offset += pageSection.scrollHeight
        return result
      })

    setPageSections(pageSectionArray)
  }

  // Set up intersection observer for each each section
  return <div ref={contentRef} className={classes.pageLayoutWrapper}>
    <ReportPageLayout mode={mode} hasPageControlBar={layout.pageControlBar !== undefined}>
      <ReportPageContextProvider page={report}>
        <div className={classes.executiveSummaryContainer} ref={containerRef} data-qa="executive-summary-container">
          {/* This section is the only piece that actually scrolls and only the header is visible. */}
          <div className={classes.scrollContainer}>
            <Header
              report={report}
              title={report.title}
              description={report.description}
              ref={headerRef}
              showScrollArrow={pageSections && pageSections.length > 0}
              onScrollArrowClick={() => scrollToSection(0)}
              highlightPageProperty={highlightPageProperty}
            />
            {/* These are invisible representations of the page sections that are scrolled to capture intersections */}
            {pageSections.map((pageSection, i) => (
              <PageSectionPlaceholder
                key={pageSection.key}
                containerRef={containerRef}
                pageSection={pageSection}
                onIntersectEnter={() => {
                  setPageSectionShowing(pageSection.key)
                  hideHeader()
                }}
                onIntersectLeaveUp={() => {
                  if (i === 0) {
                    showHeader()
                    setPageSectionShowing()
                  }
                }}
              />
            ))}
          </div>
          {/* These are the actual sections that are made visible by toggling the show class */}
          {layout.blocks.map((block, i) => (
            <PageSection
              key={block.key}
              report={report}
              block={layout.blocks[i]}
              blockIndex={i}
              isLast={i === layout.blocks.length - 1}
              pageSection={pageSections[i]}
              containerHeight={containerHeight}
              currentBreakpoint={currentBreakpoint}
              onMount={(ref) => {
                blockRefs.current[block.key] = ref
                if (Object.values(blockRefs.current).length === layout.blocks.length && !initialPagesCalculated) {
                  calculatePageSections(layout)
                  setInitialPagesCalculated(true)
                  handleBlockHeightChanges(blockRefs)
                }
              }}
              highlightBlock={highlightBlockId === block.key}
            />
          ))}
        </div>
        <ProgressSidebar
          container={containerRef && containerRef.current}
          blocks={layout.blocks}
          scrollToSection={scrollToSection}
          topContainerPadding={TOP_CONTAINER_PADDING}
        />
        { layout.pageControlBar && <div className={classes.pageControlBarContainer}><PageControlBar {...layout.pageControlBar} /></div> }
        {mode === REPORT_VIEWER_MODE.READ &&
          <FloatingBackLink showToc={true} report={report} to={routerContext.getReportListRoute(report)} />
        }
      </ReportPageContextProvider>
    </ReportPageLayout>
  </div>
}
